import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { logger } from '../../utils/logger';
import { CLIENT_ID } from '../../lib/client-context';
import { chatKeys } from './chatKeys';
import {
  ChatMessageData,
  FormattedChatMessage,
  CreateChatMessageInput,
  UpdateChatMessageInput,
  formatChatMessage,
} from './chatTypes';
import { db } from '../../lib/offline-db';
import { queueOfflineOperation } from '../../lib/offline-sync';
import { getConnectionState } from '../../contexts/ConnectionStateContext';
import { useQueryErrorHandler } from '../useQueryErrorHandler';
import { safeUpdateQueryData, batchInvalidateQueries } from '../../utils/queryInvalidationUtils';

/**
 * Hook for fetching chat messages for a specific topic
 * Supports offline mode by using IndexedDB when offline
 */
export function useChatMessages(topicId: string | null) {
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useQuery({
    queryKey: topicId ? chatKeys.byTopic(topicId) : chatKeys.lists(),
    queryFn: async () => {
      if (!topicId) {
        throw new Error('Topic ID is required');
      }

      const fetchId = Date.now().toString().slice(-4);
      logger.fetch(
        `Starting chat messages fetch #${fetchId} for topic ${topicId} (online: ${isOnline})`
      );
      const startTime = performance.now();

      try {
        // If offline, get data from IndexedDB
        if (!isOnline) {
          logger.fetch(`Offline mode: fetching chat messages from IndexedDB #${fetchId}`);

          try {
            // Get chat messages from IndexedDB
            const messages = await db.chatMessages
              .where('topic_id')
              .equals(topicId)
              .and(message => !message.isDeleted) // Filter out deleted messages
              .toArray();

            // Sort by created_at
            messages.sort((a, b) => {
              return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
            });

            logger.fetch(`Offline mode: loaded ${messages.length} chat messages from IndexedDB`);

            // Format messages using the helper function
            return messages.map(message => formatChatMessage(message as ChatMessageData));
          } catch (offlineError) {
            logger.error(`Error fetching offline chat messages #${fetchId}:`, offlineError);
            throw new Error('Failed to load chat messages from offline storage');
          }
        }

        // Online flow - fetch from server
        const { data, error } = await supabase
          .from('ai_chat_messages')
          .select('*')
          .eq('topic_id', topicId)
          .order('created_at', { ascending: true });

        if (error) throw error;

        // Store in IndexedDB for offline access
        try {
          if (data && data.length > 0) {
            await db.chatMessages.bulkPut(data);
            logger.fetch(`Stored ${data.length} chat messages in IndexedDB`);
          }
        } catch (storageError) {
          logger.error(`Error storing chat messages in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        // Format messages using the helper function
        const formattedMessages = (data || []).map(message =>
          formatChatMessage(message as ChatMessageData)
        );

        // Log performance metrics
        const endTime = performance.now();
        const duration = endTime - startTime;
        logger.fetch(`Chat messages fetch #${fetchId} completed in ${duration.toFixed(2)}ms`);

        return formattedMessages;
      } catch (error) {
        // Use the error handler to handle and log the error
        const appError = handleError(error, {
          action: 'silent',
          context: {
            fetchId,
            operation: 'fetchChatMessages',
            topicId,
            isOnline,
          },
        });

        throw appError;
      }
    },
    enabled: !!topicId,
  });
}

/**
 * Hook for fetching a single chat message by ID
 * Supports offline mode by using IndexedDB when offline
 */
export function useChatMessage(messageId: string | null) {
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useQuery({
    queryKey: messageId ? chatKeys.detail(messageId) : chatKeys.details(),
    queryFn: async () => {
      if (!messageId) {
        throw new Error('Message ID is required');
      }

      logger.fetch(`Fetching chat message ${messageId} (online: ${isOnline})`);

      try {
        // If offline, get data from IndexedDB
        if (!isOnline) {
          logger.fetch(`Offline mode: fetching chat message ${messageId} from IndexedDB`);

          try {
            // Get message from IndexedDB
            const message = await db.chatMessages.get(messageId);

            if (!message || message.isDeleted) {
              throw new Error(`Chat message ${messageId} not found in offline storage`);
            }

            // Format the message
            return formatChatMessage(message as ChatMessageData);
          } catch (offlineError) {
            logger.error(`Error fetching offline chat message ${messageId}:`, offlineError);
            throw new Error('Failed to load chat message from offline storage');
          }
        }

        // Online flow - fetch from server
        const { data, error } = await supabase
          .from('ai_chat_messages')
          .select('*')
          .eq('id', messageId)
          .single();

        if (error) throw error;

        // Store in IndexedDB for offline access
        try {
          await db.chatMessages.put(data);
        } catch (storageError) {
          logger.error(`Error storing chat message in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        // Format and return the message
        return formatChatMessage(data as ChatMessageData);
      } catch (error) {
        const appError = handleError(error, {
          action: 'silent',
          context: { operation: 'fetchChatMessage', messageId, isOnline },
        });
        throw appError;
      }
    },
    enabled: !!messageId,
  });
}

/**
 * Hook for creating a new chat message
 * Supports offline mode by storing in IndexedDB and queueing for sync
 */
export function useAddChatMessage() {
  const queryClient = useQueryClient();
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async (messageInput: CreateChatMessageInput): Promise<FormattedChatMessage> => {
      logger.store('Adding new chat message', {
        topicId: messageInput.topicId,
        role: messageInput.role,
        is_online: isOnline,
      });

      const messageId = crypto.randomUUID();
      const now = new Date().toISOString();

      // Common metadata for all messages
      const metadata = {
        client_id: CLIENT_ID,
        last_update: now,
        // Include any custom metadata that was provided
        ...(messageInput.metadata || {}),
      };

      // For user messages, include user_id; for AI messages, omit it
      const messageData: ChatMessageData =
        messageInput.role === 'user'
          ? {
              id: messageId,
              topic_id: messageInput.topicId,
              user_id: messageInput.userId || null,
              content: messageInput.content,
              role: messageInput.role,
              created_at: now,
              updated_at: now,
              highlights: [],
              summary: null,
              metadata,
            }
          : {
              id: messageId,
              topic_id: messageInput.topicId,
              content: messageInput.content,
              role: messageInput.role,
              created_at: now,
              updated_at: now,
              highlights: [],
              summary: messageInput.summary || null,
              metadata,
            };

      // If offline, store locally and queue for sync
      if (!isOnline) {
        logger.store('Offline mode: Creating chat message locally', { messageId });

        try {
          // Store in IndexedDB
          await db.chatMessages.put(messageData);

          // Queue for sync when online
          await queueOfflineOperation('ai_chat_messages', 'create', messageId, messageData);

          // Return formatted message
          return formatChatMessage(messageData);
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: { operation: 'createChatMessage', mode: 'offline' },
          });
          throw error;
        }
      }

      // Online flow - create on server
      const { data, error } = await supabase
        .from('ai_chat_messages')
        .insert([messageData])
        .select()
        .single();

      if (error) throw error;

      // Store in IndexedDB for offline access
      try {
        await db.chatMessages.put(data);
      } catch (storageError) {
        logger.error(`Error storing chat message in IndexedDB:`, storageError);
        // Continue even if storage fails - this is non-critical
      }

      // Return formatted message
      return formatChatMessage(data as ChatMessageData);
    },
    onSuccess: newMessage => {
      // Add the new message to the topic's chat messages list
      safeUpdateQueryData<FormattedChatMessage[]>(
        queryClient,
        chatKeys.byTopic(newMessage.topicId),
        currentMessages => {
          if (!currentMessages) return [newMessage];
          return [...currentMessages, newMessage];
        },
        { context: { operation: 'addChatMessage', messageId: newMessage.id } }
      );

      // Add the new message to the cache
      safeUpdateQueryData(queryClient, chatKeys.detail(newMessage.id), () => newMessage, {
        context: { operation: 'addChatMessage', messageId: newMessage.id },
      });
    },
  });
}

/**
 * Hook for updating an existing chat message
 * Supports offline mode by updating IndexedDB and queueing for sync
 */
export function useUpdateChatMessage() {
  const queryClient = useQueryClient();
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async ({
      messageId,
      topicId,
      updates,
    }: {
      messageId: string;
      topicId: string;
      updates: UpdateChatMessageInput;
    }) => {
      if (!messageId || !topicId) {
        throw new Error('Message ID and Topic ID are required');
      }

      const now = new Date().toISOString();

      // Create filtered updates object with only valid fields
      const filteredUpdates: Record<string, unknown> = {
        updated_at: now,
      };

      if (updates.content !== undefined) filteredUpdates.content = updates.content;
      if (updates.highlights) filteredUpdates.highlights = updates.highlights;
      if (updates.summary !== undefined) filteredUpdates.summary = updates.summary;

      // Get current metadata to preserve existing fields (for offline mode)
      let currentMetadata = {};

      // For online mode, we'll get the current metadata from the server
      if (isOnline) {
        try {
          const { data } = await supabase
            .from('ai_chat_messages')
            .select('metadata')
            .eq('id', messageId)
            .single();

          if (data && data.metadata) {
            currentMetadata = data.metadata;
          }
        } catch (error) {
          logger.error('Error fetching current metadata for chat message update:', error);
          // Continue with empty metadata if we can't get the current state
        }
      } else {
        // For offline mode, get current metadata from IndexedDB
        try {
          const message = await db.chatMessages.get(messageId);
          if (message && message.metadata) {
            currentMetadata = message.metadata;
          }
        } catch (error) {
          logger.error('Error fetching metadata from IndexedDB:', error);
          // Continue with empty metadata if we can't get the current state
        }
      }

      // Merge metadata
      filteredUpdates.metadata = {
        ...currentMetadata,
        ...(updates.metadata || {}),
        client_id: CLIENT_ID,
        last_update: now,
      };

      // If offline, update locally and queue for sync
      if (!isOnline) {
        logger.store('Offline mode: Updating chat message locally', { messageId });

        try {
          // Get current message from IndexedDB
          const currentMessage = await db.chatMessages.get(messageId);

          if (!currentMessage || currentMessage.isDeleted) {
            throw new Error(`Chat message ${messageId} not found in offline storage`);
          }

          // Merge updates with current data
          const updatedMessage = {
            ...currentMessage,
            ...filteredUpdates,
          };

          // Update in IndexedDB
          await db.chatMessages.put(updatedMessage);

          // Queue for sync when online
          await queueOfflineOperation('ai_chat_messages', 'update', messageId, updatedMessage);

          return { messageId, topicId, updates: filteredUpdates };
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: { operation: 'updateChatMessage', messageId, mode: 'offline' },
          });
          throw error;
        }
      }

      // Online flow - update on server
      const { data, error } = await supabase
        .from('ai_chat_messages')
        .update(filteredUpdates)
        .eq('id', messageId)
        .eq('topic_id', topicId)
        .select()
        .single();

      if (error) throw error;

      // Update in IndexedDB for offline access
      try {
        // Get current data first
        const currentMessage = await db.chatMessages.get(messageId);

        if (currentMessage) {
          await db.chatMessages.put({
            ...currentMessage,
            ...data,
          });
        }
      } catch (storageError) {
        logger.error(`Error updating chat message in IndexedDB:`, storageError);
        // Continue even if storage fails - this is non-critical
      }

      return { messageId, topicId, updates: filteredUpdates, data };
    },
    onSuccess: result => {
      const { messageId, topicId, data } = result;

      // Update message detail cache if it exists
      const updateSuccessful = safeUpdateQueryData<FormattedChatMessage>(
        queryClient,
        chatKeys.detail(messageId),
        currentMessage => {
          if (!currentMessage) return undefined;

          // If we have data from the server, use that for formatting
          if (data) {
            return formatChatMessage(data as ChatMessageData);
          }

          // Otherwise use the updates
          return {
            ...currentMessage,
            ...(result.updates.content !== undefined && {
              content: result.updates.content as string,
            }),
            ...(result.updates.highlights && { highlights: result.updates.highlights as string[] }),
            ...(result.updates.summary !== undefined && {
              summary: result.updates.summary as string | null,
            }),
            ...(result.updates.metadata && { metadata: result.updates.metadata }),
            updatedAt: result.updates.updated_at as string,
          };
        },
        { context: { operation: 'updateChatMessage', messageId } }
      );

      // If detail was updated, also update in byTopic list
      if (updateSuccessful) {
        // Update message in the topic's messages list
        safeUpdateQueryData<FormattedChatMessage[]>(
          queryClient,
          chatKeys.byTopic(topicId),
          messages => {
            if (!messages) return undefined;

            return messages.map(message => (message.id === messageId ? updateSuccessful : message));
          },
          { context: { operation: 'updateChatMessage', messageId } }
        );
      } else {
        // If the message isn't in cache or update failed, invalidate queries to refetch
        const queryKeysToInvalidate = new Map([
          [chatKeys.detail(messageId), { exact: true }],
          [chatKeys.byTopic(topicId), { exact: true }],
        ]);

        batchInvalidateQueries(queryClient, queryKeysToInvalidate, {
          refetchType: 'active', // Only refetch if someone is watching
        });
      }
    },
  });
}

/**
 * Hook for updating message highlights
 * This is a specialized version of updateChatMessage focused just on highlights
 */
export function useUpdateMessageHighlights() {
  const updateMessage = useUpdateChatMessage();

  return useMutation({
    mutationFn: async ({
      messageId,
      topicId,
      highlights,
    }: {
      messageId: string;
      topicId: string;
      highlights: string[];
    }) => {
      return updateMessage.mutateAsync({
        messageId,
        topicId,
        updates: { highlights },
      });
    },
  });
}

/**
 * Hook for deleting a chat message
 * Supports offline mode by marking as deleted in IndexedDB and queueing for sync
 * Also handles deleting paired user/assistant messages
 */
export function useDeleteChatMessage() {
  const queryClient = useQueryClient();
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async ({
      messageId,
      topicId,
      deletePaired = true,
    }: {
      messageId: string;
      topicId: string;
      deletePaired?: boolean;
    }) => {
      if (!messageId || !topicId) {
        throw new Error('Message ID and Topic ID are required');
      }

      // Create a set to track all messages being deleted in this operation
      const messagesToDelete = new Set<string>([messageId]);

      // Find paired message if enabled
      if (deletePaired) {
        try {
          // Get cached messages for this topic
          const cachedMessages = queryClient.getQueryData<FormattedChatMessage[]>(
            chatKeys.byTopic(topicId)
          );

          if (cachedMessages) {
            // Find the index of the message to delete
            const messageIndex = cachedMessages.findIndex(m => m.id === messageId);

            if (messageIndex !== -1) {
              const messageToDelete = cachedMessages[messageIndex];

              // Find paired message (if any)
              if (
                messageToDelete.role === 'user' &&
                messageIndex + 1 < cachedMessages.length &&
                cachedMessages[messageIndex + 1].role === 'assistant'
              ) {
                // User message followed by assistant message
                messagesToDelete.add(cachedMessages[messageIndex + 1].id);
              } else if (
                messageToDelete.role === 'assistant' &&
                messageIndex > 0 &&
                cachedMessages[messageIndex - 1].role === 'user'
              ) {
                // Assistant message preceded by user message
                messagesToDelete.add(cachedMessages[messageIndex - 1].id);
              }
            }
          } else if (isOnline) {
            // If no cache but online, fetch from server to find paired messages
            const { data } = await supabase
              .from('ai_chat_messages')
              .select('*')
              .eq('topic_id', topicId)
              .order('created_at', { ascending: true });

            if (data) {
              const messages = data.map(m => formatChatMessage(m as ChatMessageData));
              const messageIndex = messages.findIndex(m => m.id === messageId);

              if (messageIndex !== -1) {
                const messageToDelete = messages[messageIndex];

                // Find paired message (if any)
                if (
                  messageToDelete.role === 'user' &&
                  messageIndex + 1 < messages.length &&
                  messages[messageIndex + 1].role === 'assistant'
                ) {
                  // User message followed by assistant message
                  messagesToDelete.add(messages[messageIndex + 1].id);
                } else if (
                  messageToDelete.role === 'assistant' &&
                  messageIndex > 0 &&
                  messages[messageIndex - 1].role === 'user'
                ) {
                  // Assistant message preceded by user message
                  messagesToDelete.add(messages[messageIndex - 1].id);
                }
              }
            }
          }
        } catch (error) {
          logger.error('Error finding paired message:', error);
          // Continue with just deleting the original message if there's an error
        }
      }

      // Convert Set to Array for easier handling
      const messageIdsToDelete = Array.from(messagesToDelete);

      // If offline, mark as deleted locally and queue for sync
      if (!isOnline) {
        logger.store('Offline mode: Marking chat messages as deleted locally', {
          messageIds: messageIdsToDelete,
        });

        try {
          // Mark all messages as deleted in IndexedDB
          for (const id of messageIdsToDelete) {
            await db.chatMessages.update(id, { isDeleted: true });

            // Queue for sync when online
            await queueOfflineOperation('ai_chat_messages', 'delete', id, {
              id,
              topic_id: topicId,
            });
          }

          return { messageIds: messageIdsToDelete, topicId };
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: {
              operation: 'deleteChatMessage',
              messageId,
              mode: 'offline',
            },
          });
          throw error;
        }
      }

      // Online flow - delete on server
      // Create an array of promises for deletion operations
      const deletePromises = messageIdsToDelete.map(id =>
        supabase.from('ai_chat_messages').delete().eq('id', id).eq('topic_id', topicId)
      );

      // Execute all delete operations in parallel
      const results = await Promise.all(deletePromises);

      // Check for errors
      for (const result of results) {
        if (result.error) throw result.error;
      }

      // Remove from IndexedDB
      try {
        for (const id of messageIdsToDelete) {
          await db.chatMessages.delete(id);
        }
      } catch (storageError) {
        logger.error(`Error deleting chat messages from IndexedDB:`, storageError);
        // Continue even if storage fails - this is non-critical
      }

      return { messageIds: messageIdsToDelete, topicId };
    },
    onSuccess: result => {
      const { messageIds, topicId } = result;

      // Remove messages from cache
      for (const messageId of messageIds) {
        queryClient.removeQueries({
          queryKey: chatKeys.detail(messageId),
        });
      }

      // Update the messages list by filtering out the deleted messages
      safeUpdateQueryData<FormattedChatMessage[]>(
        queryClient,
        chatKeys.byTopic(topicId),
        oldData => {
          if (!oldData) return oldData;
          return oldData.filter(message => !messageIds.includes(message.id));
        },
        { context: { operation: 'deleteChatMessage', messageIds } }
      );
    },
  });
}

/**
 * Hook for retrying an AI response generation after an error
 * Handles deleting the error message and generating a new response
 */
export function useRetryAIResponse() {
  const deleteChatMessage = useDeleteChatMessage();
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;

  return useMutation({
    mutationFn: async ({
      topicId,
      errorMessageId,
    }: {
      topicId: string;
      errorMessageId: string;
      model?: string;
    }) => {
      // First, delete the error message
      await deleteChatMessage.mutateAsync({
        messageId: errorMessageId,
        topicId,
        deletePaired: false, // Don't delete the user message
      });

      // For offline mode, we only delete the error message
      // AI response generation requires online connectivity
      if (!isOnline) {
        return { success: false, message: 'Cannot generate AI response while offline' };
      }

      // The actual retry logic will be handled in the useChatWithAI hook's generateAIResponse
      // function, which will be called from the component after this mutation completes
      return { success: true };
    },
  });
}
